import React, { useEffect, useState } from "react";
import { isEmpty } from "@helpers/common";

const isValidDate = (value) => {
    const parts = value.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Check the ranges of month and year
    if (year < 1000 || year > 3000 || month == 0 || month > 12) {
        return false;
    }

    var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
        monthLength[1] = 29;
    }

    return day > 0 && day <= monthLength[month - 1]
        ? new Date(year, month, day)
        : false;
};

function isOverTheAge(date, minAge) {
    const now = new Date();

    const currentYear = now.getFullYear();
    const dobYear = date.getFullYear();

    const yearDiff = currentYear - dobYear;

    if (yearDiff > minAge) return true;
    if (yearDiff < minAge) return false;

    const currentMonth = now.getMonth() + 1;
    const dobMonth = date.getMonth();

    const monthDiff = currentMonth - dobMonth;

    if (monthDiff > 0) return true;
    if (monthDiff < 0) return false;

    const currentDay = now.getDate();
    const dobDay = date.getDate();
    const dayDiff = currentDay - dobDay;
    return dayDiff > 0;
}

const useInput = (options) => {
    const [name, setName] = useState(options?.name);
    const [value, setValue] = useState(options?.value ?? "");
    const [isTouched, setIsTouched] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (options.skipValidationOnLoad) return;
        validate(value);
    }, []);

    const onChange = (e) => handleChange(e);

    const onBlur = (e) => {
        if (options.format) {
            const formattedValue = options.format(value);
            setCurrentValue(formattedValue);
        }

        validate(value);
        setIsTouched(true);
    };

    const reset = () => {
        setName(options?.name);
        setValue(options?.value ?? "");
        setIsTouched(false);
        setIsValidating(false);
        setIsValid(false);
        setError(null);
    };

    const setCurrentValue = (value) => setValue(value);

    const setInvalidState = (error) => {
        setIsValid(false);
        setError(error);
    };

    const setTouched = () => setIsTouched(true);

    const handleChange = (e) => {
        if (e) {
            let newValue = e.target.value;

            if (options.filter) {
                newValue = options.filter(value, newValue);
            }

            setValue(newValue);
            validate(newValue);
        } else {
            validate(value);
        }
    };

    const isInt = (value) => {
        return /^\d+$/.test(value) === true;
    };

    const validate = (value) => {
        if (isValidating) return;

        setIsValidating(true);

        const isValueNumber = parseFloat(value) !== NaN;

        const isValueEmpty = isEmpty(value);

        const length = isValueEmpty
            ? 0
            : isValueNumber
            ? `${value}`.length
            : value.trim().length;

        if (length === 0 && options.required === true) {
            setInvalidState(options.requiredError);
            setIsValidating(false);
            return;
        }

        if (length === 0) {
            setIsValidating(false);
            if (options.required === false) {
                setIsValid(true);
            }
            return;
        }

        if (options.minLength && length < options.minLength) {
            setInvalidState(options.minLengthError);
            setIsValidating(false);
            return;
        }

        if (options.maxLength && length > options.maxLength) {
            setInvalidState(options.maxLengthError);
            setIsValidating(false);
            return;
        }

        if (options.isInteger && !isInt(value)) {
            setInvalidState(options.isIntegerError);
            setIsValidating(false);
            return;
        }

        if (isInt(options.min) && isInt(options.max)) {
            const intValue = parseFloat(value);
            if (intValue < options.min || intValue > options.max) {
                setInvalidState(options.rangeError);
                setIsValidating(false);
                return;
            }
        }

        if (options.regex) {
            const isValid = new RegExp(options.regex).test(value);
            if (!isValid) {
                setInvalidState(options.regexError);
                setIsValidating(false);
                return;
            }
        }

        if (options.minAgeYear || options.invalidDateError) {
            const validDate = isValidDate(value);

            if (!validDate) {
                setInvalidState(options.invalidDateError);
                setIsValidating(false);
                return;
            }

            if (options.minAgeYear) {
                let isAdult = isOverTheAge(validDate, options.minAgeYear);
                if (!isAdult) {
                    setInvalidState(options.minAgeYearError);
                    setIsValidating(false);
                    return;
                }
            }
        }

        if (options.validate) {
            const error = options.validate(value);
            if (error) {
                setInvalidState(error);
                setIsValidating(false);
                return;
            }
        }

        if (options.validateAsync) {
            options.validateAsync(value).then((error) => {
                if (error) setInvalidState(error);
                else {
                    setIsValid(true);
                    setError("");
                }
                setIsValidating(false);
            });
            return;
        }

        setIsValidating(false);
        setIsValid(true);
        setError("");
    };

    const errorToDisplay = error && isTouched ? error : null;

    return {
        name,
        value,
        isTouched,
        isValid,
        error,
        errorToDisplay,
        isValidating,
        onChange,
        onBlur,
        reset,
        setValue: setCurrentValue,
        setTouched: setTouched,
        initValue: (initValue) => {
            setCurrentValue(initValue);
            validate(initValue);
            setIsTouched(true);
        },
        isValidState: () => isValid && !isValidating,
    };
};

export default useInput;
